import type { DictationAdapter } from "@assistant-ui/react";
import { message } from "antd";
import { type MicCapture, startMicCapture } from "./micCapture";

type Unsubscribe = () => void;

export interface DictationCredentials {
  token: string;
  url: string;
}

export interface TransportEvents {
  onInterim(text: string): void;
  onFinal(text: string): void;
  /** The connection dropped before finish(). */
  onError(): void;
}

/** Streams 16 kHz PCM to a speech-to-text provider and reports text back. */
export interface DictationTransport {
  /** Resolves once the provider is ready to receive audio. */
  open(credentials: DictationCredentials, events: TransportEvents): Promise<void>;
  send(frame: ArrayBuffer): void;
  /** Asks the provider for its final text on everything sent, then closes. */
  finish(): Promise<void>;
  close(): void;
}

// ── What the composer's recording bar reads ──────────────────────────────

export interface ActiveDictation {
  startedAt: number;
  getLevel(): number;
  discard(): void;
}

let active: ActiveDictation | null = null;
const listeners = new Set<() => void>();

export const activeDictation = {
  get: () => active,
  subscribe(listener: () => void): Unsubscribe {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

function publish(next: ActiveDictation | null) {
  active = next;
  listeners.forEach((listener) => listener());
}

// ── Credentials are fetched ahead of the click ───────────────────────────

export class CredentialCache {
  private pending: Promise<DictationCredentials> | null = null;
  private fetchedAt = 0;

  constructor(
    private fetchCredentials: () => Promise<DictationCredentials>,
    private maxAgeMs: number,
  ) {}

  prefetch() {
    if (this.pending && Date.now() - this.fetchedAt < this.maxAgeMs) return;
    this.fetchedAt = Date.now();
    this.pending = this.fetchCredentials();
    this.pending.catch(() => {
      this.pending = null;
    });
  }

  take(): Promise<DictationCredentials> {
    this.prefetch();
    return this.pending as Promise<DictationCredentials>;
  }
}

// ── Session ──────────────────────────────────────────────────────────────

export class LiveDictationSession implements DictationAdapter.Session {
  status: DictationAdapter.Status = { type: "starting" };

  private speechStartCallbacks: Array<() => void> = [];
  private speechEndCallbacks: Array<(result: DictationAdapter.Result) => void> = [];
  private speechCallbacks: Array<(result: DictationAdapter.Result) => void> = [];

  private capture: MicCapture | null = null;
  private committed = "";
  private interim = "";
  private connected = false;
  private discarded = false;
  private ended = false;
  private published: ActiveDictation | null = null;
  private startup: Promise<void>;

  constructor(
    private transport: DictationTransport,
    credentials: Promise<DictationCredentials>,
  ) {
    this.startup = this.start(credentials);
  }

  private async start(credentials: Promise<DictationCredentials>) {
    // Connect and open the mic in parallel; frames buffer until the socket is ready, so nothing said is lost.
    const opening = credentials.then((c) =>
      this.transport.open(c, {
        onInterim: (text) => this.handleInterim(text),
        onFinal: (text) => this.handleFinal(text),
        onError: () => this.handleError(),
      }),
    );
    opening.catch(() => {});
    try {
      this.capture = await startMicCapture();
    } catch (err) {
      const denied = err instanceof DOMException && err.name === "NotAllowedError";
      message.error(denied ? "Allow microphone access to use voice input." : "Couldn't start the microphone.");
      this.end("error");
      return;
    }
    if (this.ended) {
      await this.capture.stop();
      return;
    }

    this.status = { type: "running" };
    this.published = {
      startedAt: Date.now(),
      getLevel: () => this.capture?.getLevel() ?? 0,
      discard: () => {
        this.discarded = true;
      },
    };
    publish(this.published);
    for (const cb of this.speechStartCallbacks) cb();

    try {
      await opening;
    } catch {
      if (!this.ended) message.error("Couldn't connect voice input. Please try again.");
      this.end("error");
      return;
    }
    if (this.ended) return;
    this.connected = true;
    this.capture.setSink((frame) => this.transport.send(frame));
  }

  private handleInterim(text: string) {
    this.interim = text;
    this.emit(false);
  }

  private handleFinal(text: string) {
    if (text) this.committed = this.committed ? `${this.committed} ${text}` : text;
    this.interim = "";
    this.emit(false);
  }

  private handleError() {
    if (this.ended) return;
    message.error("Voice input disconnected. What you said so far was kept.");
    this.commitAndEnd("error");
  }

  private get text() {
    return [this.committed, this.interim].filter(Boolean).join(" ");
  }

  private get result() {
    return this.discarded ? "" : this.text;
  }

  private emit(isFinal: boolean) {
    for (const cb of this.speechCallbacks) cb({ transcript: this.result, isFinal });
  }

  private commitAndEnd(reason: "stopped" | "error") {
    if (this.ended) return;
    this.emit(true);
    for (const cb of this.speechEndCallbacks) cb({ transcript: this.result, isFinal: true });
    this.end(reason);
  }

  private end(reason: "stopped" | "cancelled" | "error") {
    if (this.ended) return;
    this.ended = true;
    this.status = { type: "ended", reason };
    this.transport.close();
    void this.capture?.stop();
    this.capture = null;
    if (active === this.published) publish(null);
  }

  async stop(): Promise<void> {
    if (this.ended) return;
    await this.startup;
    if (this.ended) return;
    const capture = this.capture;
    this.capture = null;
    if (capture) await capture.stop();
    if (this.connected && !this.discarded) await this.transport.finish();
    this.commitAndEnd("stopped");
  }

  cancel(): void {
    this.end("cancelled");
  }

  onSpeechStart(callback: () => void): Unsubscribe {
    this.speechStartCallbacks.push(callback);
    return () => {
      this.speechStartCallbacks = this.speechStartCallbacks.filter((cb) => cb !== callback);
    };
  }

  onSpeechEnd(callback: (result: DictationAdapter.Result) => void): Unsubscribe {
    this.speechEndCallbacks.push(callback);
    return () => {
      this.speechEndCallbacks = this.speechEndCallbacks.filter((cb) => cb !== callback);
    };
  }

  onSpeech(callback: (result: DictationAdapter.Result) => void): Unsubscribe {
    this.speechCallbacks.push(callback);
    return () => {
      this.speechCallbacks = this.speechCallbacks.filter((cb) => cb !== callback);
    };
  }
}
